import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  IconButton,
  List,
  Typography,
  Chip,
  Button,
  Collapse,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import useUploadStore, { IUploadFile } from '@/store/media/useUploadStore';
import UploadItem from '@/content/components/common/UploadManager/UploadItem';

const UploadManager = () => {
  const theme = useTheme();
  const { t: tCommon } = useTranslation('common');

  const [isExpanded, setIsExpanded] = useState(true);

  const uploads = useUploadStore((state) => state.uploads);
  const isManagerOpen = useUploadStore((state) => state.isManagerOpen);
  const closeManager = useUploadStore((state) => state.closeManager);
  const clearCompleted = useUploadStore((state) => state.clearCompleted);

  const {
    activeUploads, completedUploads, failedUploads, cancelledUploads, pendingUploads,
  } = useMemo(() => uploads.reduce(
    (acc, u) => {
      if (u.status === 'uploading') acc.activeUploads.push(u);
      else if (u.status === 'completed') acc.completedUploads.push(u);
      else if (u.status === 'failed') acc.failedUploads.push(u);
      else if (u.status === 'cancelled') acc.cancelledUploads.push(u);
      else if (u.status === 'pending') acc.pendingUploads.push(u);
      return acc;
    },
    {
      activeUploads: [] as IUploadFile[],
      completedUploads: [] as IUploadFile[],
      failedUploads: [] as IUploadFile[],
      cancelledUploads: [] as IUploadFile[],
      pendingUploads: [] as IUploadFile[],
    },
  ), [uploads]);

  const totalUploads = uploads.length;
  const hasCompletedOrFailedOrCancelled = completedUploads.length > 0
   || failedUploads.length > 0 || cancelledUploads.length > 0;

  const hasStatus = activeUploads.length > 0
  || completedUploads.length > 0
  || failedUploads.length > 0
  || cancelledUploads.length > 0;
  if (!isManagerOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 400,
        maxWidth: 'calc(100vw - 48px)',
        zIndex: 1300,
      }}
    >
      <Card
        elevation={8}
        sx={{
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon fontSize="small" color="primary" aria-label="upload-icon" />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
              }}
            >
              {tCommon('UPLOAD_MANAGER.TITLE')}
            </Typography>
            <Chip
              label={`${activeUploads.length + pendingUploads.length}/${totalUploads}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? 'Collapse upload manager' : 'Expand upload manager'}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <IconButton size="small" onClick={closeManager} aria-label="Close upload manager">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Collapse in={isExpanded}>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            <List dense sx={{ p: 1 }}>
              {uploads?.map((upload) => (
                <UploadItem key={upload.id} upload={upload} />
              ))}
            </List>
          </Box>

          {/* Actions */}
          {hasCompletedOrFailedOrCancelled && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              <Button
                size="small"
                startIcon={<ClearIcon aria-label="clear-icon" />}
                onClick={clearCompleted}
                variant="text"
                color="secondary"
              >
                {tCommon('UPLOAD_MANAGER.CLEAR_BTN')}
              </Button>
            </Box>
          )}
        </Collapse>

        {/* Minimized view */}
        {!isExpanded && (
          <Box sx={{ px: 2, py: 1 }}>
            {hasStatus && (
            <Typography
              role="status"
              aria-live="polite"
              aria-atomic="true"
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {activeUploads.length > 0 && `${activeUploads.length} ${tCommon('UPLOAD_MANAGER.STATUS.UPLOADING')}`}
              {completedUploads.length > 0 && ` • ${completedUploads.length} ${tCommon('UPLOAD_MANAGER.STATUS.COMPLETED')}`}
              {failedUploads.length > 0 && ` • ${failedUploads.length} ${tCommon('UPLOAD_MANAGER.STATUS.FAILED')}`}
              {cancelledUploads.length > 0 && ` • ${cancelledUploads.length} ${tCommon('UPLOAD_MANAGER.STATUS.CANCELLED')}`}
            </Typography>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default UploadManager;
